/**
 * Custom Select Component
 * For language and currency selectors
 * Uses connectedCallback to ensure children are available
 */

if (!customElements.get('custom-select')) {
  customElements.define(
    'custom-select',
    class CustomSelect extends HTMLElement {
      connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;

        this.elements = {
          input: this.querySelector('input[type="hidden"]'),
          button: this.querySelector('[data-custom-select-trigger]'),
          panel: this.querySelector('.custom-select__list-wrapper'),
          filterInput: this.querySelector('[data-custom-select-filter]'),
          filterReset: this.querySelector('.custom-select__filter-reset'),
          closeBtn: this.querySelector('[data-custom-select-close]'),
          overlay: this.querySelector('.custom-select__overlay'),
        };

        if (!this.elements.button || !this.elements.panel) return;

        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
        this.addEventListener('keydown', this.onContainerKeyDown.bind(this));
        this.addEventListener('focusout', this.closeSelector.bind(this));
        this.elements.button.addEventListener('click', this.openSelector.bind(this));

        this.querySelectorAll('[data-custom-select-option]').forEach((item) =>
          item.addEventListener('click', this.onItemClick.bind(this))
        );

        if (this.elements.filterInput) {
          this.elements.filterInput.addEventListener('input', this.onFilterInput.bind(this));
          this.elements.filterInput.addEventListener('change', (e) => e.stopPropagation());
          if (this.elements.filterReset) {
            this.elements.filterReset.addEventListener('click', this.onFilterReset.bind(this));
          }
        }

        if (this.elements.closeBtn) {
          this.elements.closeBtn.addEventListener('click', () => this.hidePanel());
        }
        if (this.elements.overlay) {
          this.elements.overlay.addEventListener('click', () => this.hidePanel());
        }
      }

      hidePanel() {
        this.elements.button.setAttribute('aria-expanded', 'false');
        this.elements.panel.setAttribute('hidden', true);
        if (this.elements.filterInput) {
          this.elements.filterInput.value = '';
          this.onFilterInput();
        }
      }

      onContainerKeyDown(event) {
        const focusableItems = Array.from(this.querySelectorAll('[data-custom-select-option]'));
        let focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
        let itemToFocus;

        switch (event.code.toUpperCase()) {
          case 'ARROWUP':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
            itemToFocus.focus();
            break;
          case 'ARROWDOWN':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
            itemToFocus.focus();
            break;
        }
      }

      onContainerKeyUp(event) {
        if (event.code.toUpperCase() !== 'ESCAPE') return;
        if (this.elements.button.getAttribute('aria-expanded') === 'false') return;

        this.hidePanel();
        event.stopPropagation();
        this.elements.button.focus();
      }

      onItemClick(event) {
        event.preventDefault();
        const selectedValue = event.currentTarget.dataset.value;
        const selectedText = event.currentTarget.querySelector('.ff-button-text')?.textContent.trim() || event.currentTarget.textContent.trim();

        if (this.elements.input) {
          this.elements.input.value = selectedValue;
        }

        const buttonText = this.elements.button.querySelector('.ff-button-text');
        if (buttonText) {
          buttonText.textContent = selectedText;
        }

        this.querySelectorAll('[data-custom-select-option]').forEach((opt) => {
          if (opt.dataset.value === selectedValue) {
            opt.setAttribute('aria-current', 'true');
          } else {
            opt.removeAttribute('aria-current');
          }
        });

        this.hidePanel();

        this.dispatchEvent(new CustomEvent('change', {
          bubbles: true,
          detail: { value: selectedValue, text: selectedText }
        }));
      }

      onFilterInput() {
        const query = (this.elements.filterInput?.value || '').toLowerCase().trim();
        const items = this.querySelectorAll('.custom-select__item');

        items.forEach((item) => {
          const text = item.textContent.toLowerCase();
          item.style.display = !query || text.includes(query) ? '' : 'none';
        });

        if (this.elements.filterReset) {
          this.elements.filterReset.hidden = !query;
        }
      }

      onFilterReset() {
        if (this.elements.filterInput) {
          this.elements.filterInput.value = '';
          this.elements.filterInput.focus();
        }
        this.onFilterInput();
      }

      openSelector() {
        this.elements.button.focus();
        this.elements.panel.toggleAttribute('hidden');
        this.elements.button.setAttribute(
          'aria-expanded',
          (this.elements.button.getAttribute('aria-expanded') === 'false').toString()
        );

        if (this.elements.filterInput && this.elements.button.getAttribute('aria-expanded') === 'true') {
          requestAnimationFrame(() => this.elements.filterInput.focus());
        }
      }

      closeSelector(event) {
        if (!event.relatedTarget || !this.contains(event.relatedTarget)) {
          this.hidePanel();
        }
      }
    }
  );
}
